import axios from '../utils/axiosConfig';
import { Snippet, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class SnippetsService {
  private baseURL = `${API_BASE_URL}/snippets`;

  // Get all Snippets
  async getAllSnippets(includeHidden: boolean = false, category?: string): Promise<Snippet[]> {
    try {
      let url = this.baseURL;
      const params = new URLSearchParams();
      
      if (includeHidden) params.append('includeHidden', 'true');
      if (category) params.append('category', category);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get<ApiListResponse<Snippet>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch snippets');
    } catch (error) {
      console.error('Error fetching snippets:', error);
      throw error;
    }
  }

  // Get visible Snippets (for homepage)
  async getVisibleSnippets(category?: string): Promise<Snippet[]> {
    return this.getAllSnippets(false, category);
  }

  // Get snippets by category
  async getSnippetsByCategory(category: string, includeHidden: boolean = false): Promise<Snippet[]> {
    return this.getAllSnippets(includeHidden, category);
  }

  // Get Snippet by ID
  async getSnippetById(id: number): Promise<Snippet> {
    try {
      const response = await axios.get<ApiResponse<Snippet>>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch snippet');
    } catch (error) {
      console.error('Error fetching snippet:', error);
      throw error;
    }
  }

  // Create new Snippet
  async createSnippet(formData: FormData): Promise<Snippet> {
    try {
      const response = await axios.post<ApiResponse<Snippet>>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create snippet');
    } catch (error) {
      console.error('Error creating snippet:', error);
      throw error;
    }
  }

  // Update Snippet
  async updateSnippet(id: number, formData: FormData): Promise<Snippet> {
    try {
      const response = await axios.put<ApiResponse<Snippet>>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update snippet');
    } catch (error) {
      console.error('Error updating snippet:', error);
      throw error;
    }
  }

  // Delete Snippet
  async deleteSnippet(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete snippet');
      }
    } catch (error) {
      console.error('Error deleting snippet:', error);
      throw error;
    }
  }

  // Toggle visibility
  async toggleVisibility(id: number): Promise<Snippet> {
    try {
      const response = await axios.patch<ApiResponse<Snippet>>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // Helper method to get image URL
  getImageUrl(imagePath: string | undefined | null): string {
    if (!imagePath || typeof imagePath !== 'string') {
      return '/assets/images/default-snippet.jpg';
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // If it's a relative path starting with /uploads or /assets, return as is (proxy will handle it)
    if (imagePath.startsWith('/uploads') || imagePath.startsWith('/assets')) {
      return imagePath;
    }
    
    // Otherwise assume it's just a filename, prepend /uploads/
    return `/uploads/${imagePath}`;
  }

  // Helper method to get video URL
  getVideoUrl(videoPath: string | undefined | null): string {
    if (!videoPath || typeof videoPath !== 'string') {
      return '';
    }
    
    // If it's already a full URL, return as is
    if (videoPath.startsWith('http')) {
      return videoPath;
    }
    
    // If it's a relative path starting with /uploads or /assets, return as is (proxy will handle it)
    if (videoPath.startsWith('/uploads') || videoPath.startsWith('/assets')) {
      return videoPath;
    }
    
    // Otherwise assume it's just a filename, prepend /uploads/
    return `/uploads/${videoPath}`;
  }

  // Get category badge color
  getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
      'image': '#3498db',
      'ebook': '#9b59b6',
      'video': '#e74c3c'
    };
    return colors[category] || '#95a5a6';
  }

  // Get category icon
  getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      'image': '🖼️',
      'ebook': '📚',
      'video': '🎥'
    };
    return icons[category] || '📄';
  }
}

export default new SnippetsService();
